import { resolveBraceVars, snowyVarCatalog } from "@swivl-mono/helpers";
import { CortexSetting, LocationVarInheritance } from "@swivl-mono/types";
import { BasicSelect, VariableTypeahead } from "@swivl-mono/ui";

import { SavedIndicator } from "../../../../Components/SavedIndicator/SavedIndicator";
import { WidgetSwitch } from "../../../Widgets/WidgetSwitch";
import { useTopicContext, useTopicHandlers, useTopicRow } from "../Hooks/useTopicContext";
import { CortexInheritancePill } from "./CortexInheritancePill";
import { CortexRowShell } from "./CortexRowShell";
import { RowInheritanceControls } from "./RowInheritanceControls";
import { CORTEX_FIELD_CLASS, CORTEX_TEXTAREA_CLASS } from "./cortexFieldClass";

/**
 * The value as an operator reads it — used both for the read-mode text and the
 * "Reverts to: …" tooltip. Toggles are stored as "1"/"0" here (see `defaultAsVar`
 * below), not "true"/"false". Returns "" for a blank value so each caller can pick its
 * own placeholder.
 */
function settingText(setting: CortexSetting, value: string): string {
  if (setting.kind === "toggle") return value === "1" ? "On" : "Off";
  if (setting.kind === "select") return setting.options.find((option) => option.value === value)?.label ?? value;
  if (setting.kind === "variable-textarea") return variableDisplayText(value);
  return value.trim();
}

/** Read mode shows `{{Type:Key}}` tokens by their display names, the way the pills do. */
function variableDisplayText(value: string): string {
  const displays = Object.fromEntries(snowyVarCatalog().map((tag) => [tag.id, `{${tag.display}}`]));
  return resolveBraceVars(value, displays).text.trim();
}

interface CortexSettingRowProps {
  setting: CortexSetting;
  /**
   * The topic's other settings, so `setting.showIf` can find the sibling it depends on by `key`.
   * Without this a `showIf` is ignored — which is what every caller got until 2026-08-30, when the
   * Storagely / StoragePug fields declared one and it did nothing.
   */
  settings?: CortexSetting[];
  /**
   * "horizontal" puts the label, control and pill on one line (the Policies page
   * grammar). Defaults to the stacked layout the Details v3 pages use.
   */
  layout?: "stacked" | "horizontal";
  /** Fires alongside the value change — e.g. to sync a sibling var. */
  onValueChange?: (value: string) => void;
}

/**
 * Typed-setting editor (toggle / select / text / number / textarea). Saves route
 * directly to LocationVariables - no formatter.
 *
 * Pulls all live state (merged, draft, handlers) from the topic context.
 */
export function CortexSettingRow({ setting, settings, layout = "stacked", onValueChange }: CortexSettingRowProps): React.ReactElement | null {
  const { merged, draft } = useTopicRow(setting.varKey);
  const handlers = useTopicHandlers(setting.varKey);
  const ctx = useTopicContext();
  const { savedVisible, parentMerged, isEditing, explicitEditing } = ctx;

  // `showIf`: the row exists only while the sibling it depends on has the given value — a
  // provider-specific id appears only for that provider. Reads the sibling's live value (its
  // draft first, so the row appears the moment the select changes, before Save).
  const dependsOn = setting.showIf ? settings?.find((s) => s.key === setting.showIf?.key) : undefined;
  if (setting.showIf && dependsOn) {
    const stored = ctx.draft[dependsOn.varKey]?.value ?? ctx.merged[dependsOn.varKey]?.value;
    const siblingDefault = dependsOn.kind === "toggle" ? (dependsOn.default ? "1" : "0") : String(dependsOn.default ?? "");
    const current = stored ?? siblingDefault;
    const wanted = typeof setting.showIf.equals === "boolean" ? (setting.showIf.equals ? "1" : "0") : String(setting.showIf.equals);
    if (current !== wanted) return null;
  }

  const draftValue = draft?.value;
  const isOverride = merged.inheritance === LocationVarInheritance.NONE;
  const overrideStaged = draftValue !== undefined;
  // With an Edit button in play, Edit is the only gate and changing a field is what
  // creates the override (same as the Policies card). Auto-save topics have no edit
  // step, so they keep the per-row Customize gate.
  const editable = explicitEditing ? isEditing : isOverride || overrideStaged;

  // The value the field displays: draft first, then the override row, then the
  // inherited value (from parent scope or VarDef default). Toggle defaults are
  // booleans in the catalog but stored as "1"/"0" like every other boolean
  // location var — serialize accordingly.
  const defaultAsVar = setting.kind === "toggle" ? (setting.default ? "1" : "0") : String(setting.default ?? "");
  const displayValue = draftValue ?? merged.value ?? defaultAsVar;

  // Where Revert lands: discarding a staged edit returns to this scope's saved
  // state; reverting a saved override falls through to the parent scope, or to the
  // catalog default at org scope where parentMerged is undefined.
  const revertTarget = overrideStaged ? (merged.value ?? defaultAsVar) : (parentMerged?.[setting.varKey]?.value ?? defaultAsVar);

  const setValue = (value: string): void => {
    handlers.onSettingValue(value);
    onValueChange?.(value);
  };

  // Read mode shows the pill alone: Revert is an edit action, so it belongs with the
  // other edit affordances rather than sitting on a row you cannot change.
  const readOnly = explicitEditing && !isEditing;
  const inheritanceControls = readOnly ? (
    <CortexInheritancePill inheritance={isOverride || overrideStaged ? LocationVarInheritance.NONE : merged.inheritance} />
  ) : (
    <RowInheritanceControls
      inheritance={merged.inheritance}
      overrideStaged={overrideStaged}
      revertsTo={settingText(setting, revertTarget) || "(empty)"}
      showCustomize={!explicitEditing}
      onCustomize={() => handlers.onCustomize(merged.value ?? defaultAsVar, false)}
      onRevert={() => handlers.onRevert(merged)}
      onDiscard={handlers.onDiscard}
    />
  );

  return (
    <CortexRowShell
      label={setting.label}
      description={setting.description}
      titleStatus={savedVisible !== undefined ? <SavedIndicator visible={savedVisible} /> : undefined}
      helpUrl={setting.helpUrl}
      inheritance={merged.inheritance}
      overrideStaged={overrideStaged}
      revertsTo={settingText(setting, revertTarget) || "(empty)"}
      showCustomize={!explicitEditing}
      onCustomize={() => handlers.onCustomize(merged.value ?? defaultAsVar, false)}
      onRevert={() => handlers.onRevert(merged)}
      onDiscard={handlers.onDiscard}
      layout={layout}
      controls={inheritanceControls}
    >
      {readOnly ? (
        <span className="body-sm break-words text-grey-900">{settingText(setting, displayValue) || "—"}</span>
      ) : (
        <>
          {setting.kind === "toggle" && (
            // Flex, not block: the switch is inline-level, so a block wrapper would give it
            // a text line box taller than the control and knock it off the row's centre line.
            <span className="flex items-center">
              <WidgetSwitch on={displayValue === "1"} disabled={!editable} onSet={(next) => setValue(next ? "1" : "0")} ariaLabel={setting.label} />
            </span>
          )}
          {setting.kind === "select" && (
            <BasicSelect
              name={setting.key}
              disabled={!editable}
              hideClear={true}
              selectedValues={displayValue ? [displayValue] : []}
              options={setting.options.map((option) => ({ label: option.label, value: option.value }))}
              onChange={(selected) => {
                if (selected[0] !== undefined) {
                  setValue(selected[0]);
                }
              }}
            />
          )}
          {setting.kind === "text" && (
            <input
              type="text"
              disabled={!editable}
              className={CORTEX_FIELD_CLASS}
              value={displayValue}
              placeholder={setting.placeholder}
              onChange={(e) => setValue(e.target.value)}
            />
          )}
          {setting.kind === "number" && (
            <input
              type="number"
              disabled={!editable}
              className={CORTEX_FIELD_CLASS}
              value={displayValue}
              min={setting.min}
              max={setting.max}
              step={setting.step}
              onChange={(e) => setValue(e.target.value)}
            />
          )}
          {setting.kind === "variable-textarea" && (
            <VariableTypeahead
              name={setting.key}
              value={displayValue}
              placeholder={setting.placeholder}
              disabled={!editable}
              onChange={(next) => setValue(next)}
            />
          )}
          {setting.kind === "textarea" && (
            <textarea
              disabled={!editable}
              className={CORTEX_TEXTAREA_CLASS}
              value={displayValue}
              rows={setting.rows}
              placeholder={setting.placeholder}
              onChange={(e) => setValue(e.target.value)}
            />
          )}
        </>
      )}
    </CortexRowShell>
  );
}
